/**
 * API client for Centre Operations -> Incident Reports and Underage Consent.
 *
 * Backend: oh_operations, alfred-suffixed endpoints. Every `apiEndpoints.X`
 * reference lives INSIDE a function body (TDZ-safety — mirrors
 * parentOnboarding.js / checklist.js).
 *
 * Each call returns `response.data` — the unwrapped body — exactly like
 * parentOnboarding.js. The request helper resolves to the FULL axios response,
 * so a wrapper that returned `response` handed the caller `{data, status, …}`
 * instead of the array/object it expected. That is what made a just-submitted
 * incident report vanish: the list ran `Array.isArray(response)` (always false)
 * and rendered empty, and the child-search read `response.children` (undefined)
 * off the wrong object.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

const fetchIncidentReports = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.incidentReport}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const createIncidentReport = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.incidentReport}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const updateIncidentReport = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.incidentReport}/${uuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const deleteIncidentReport = async (uuid) => {
  try {
    const response = await remove(
      `${apiEndpoints.incidentReport}/${uuid}/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const fetchUnderageConsents = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.underageConsent}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/* Programmes (from Standard, so they match the age policy), the academic year
   TODAY falls in, and — when a phone is given — that number's children, so the
   form can fill itself in instead of being typed. */
const fetchUnderageOptions = async (phone, centre, q) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.underageConsent}/options/alfred`, {
        phone,
        centre,
        q
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const createUnderageConsent = async (payload) => {
  try {
    const response = await post(
      `${apiEndpoints.underageConsent}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const deleteUnderageConsent = async (uuid) => {
  try {
    const response = await remove(
      `${apiEndpoints.underageConsent}/${uuid}/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

/* The PARENT's page. The token is the authorisation — the parent has no Alfred
   login, and these two calls are the only ones the public route makes. */
const fetchPublicConsent = async (token) => {
  try {
    const response = await get(
      `${apiEndpoints.underageConsent}/public/${token}`
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const signPublicConsent = async (token, payload) => {
  try {
    const response = await post(
      `${apiEndpoints.underageConsent}/public/${token}`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

export {
  fetchIncidentReports,
  createIncidentReport,
  updateIncidentReport,
  deleteIncidentReport,
  fetchUnderageConsents,
  fetchUnderageOptions,
  createUnderageConsent,
  deleteUnderageConsent,
  fetchPublicConsent,
  signPublicConsent
}
